/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ref, onValue } from 'firebase/database';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// @mui material components
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Fab from '@mui/material/Fab';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import i18n from 'components/Translation/Translate';

// Authentication layout components
import * as Defi from 'services/Define';
import * as Func from 'services/Function';
import db from 'services/Firebase';
import PageLayout from 'examples/LayoutContainers/PageLayout';
import { FetchData } from 'services/Request';
import nopic from 'assets/images/nopic.jpg';

import './PointOfSale.css';

const initAttr = {
  id: '',
  code: '',
  name: '',
  shortcode: '',
  poscart_height: '500',
  poscampaign_height: '300',
  image: nopic,
  signature: nopic,
  image_postop: nopic,
  image_posright: nopic,
  image_posbottom: nopic,
  pos_bg: '#FFFFFF',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initCart = {
  id: '',
  customer_id: '',
  customer_name: '',
  nettotal: '',
  money_change: '',
};

function PosMiniScreen() {
  const { t } = useTranslation();
  const { id } = useParams();
  const app = useSelector((state) => state[Defi.APP_STATE]);
  const [attr, setAttr] = useState(initAttr);
  const [cart, setCart] = useState(initCart);
  const [cartList, setCartList] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const [vdo, setVdo] = useState({ id: '', filepath: '' });
  const [promotion, setPromotion] = useState([]);

  const handleCart = async () => {
    if (app.device !== '') {
      onValue(ref(db, `carts/${app.device}`), (snapshot) => {
        setCartList([]);
        const data = snapshot.val();

        if (data.data !== undefined) {
          setCart(data.data);
        } else {
          setCart(initAttr);
        }

        if (data.list !== undefined) {
          setCartList(data.list);
        } else {
          setCartList([]);
        }

        if (data.campaign !== undefined) {
          setCampaign(data.campaign);
        } else {
          setCampaign([]);
        }
      });
    }
  };

  const init = () => {
    document.title = 'POS MINI';
    FetchData('GET', `/posmini`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setVdo(result.promotion);
        setPromotion(result.campaign);
        setAttr(result.data);
      } else {
        setCart(initCart);
        setCartList([]);
        setCampaign([]);
      }
    });
  };

  const renderOrder = () => {
    return (
      <TableContainer sx={{ height: attr.poscart_height * 1, boxShadow: 0, m: 0, p: 0 }}>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell align='right' className='nowrap'>
                Price
              </TableCell>
              <TableCell align='right'>Quantity</TableCell>
              <TableCell align='left'>Unit</TableCell>
              <TableCell align='right'>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cartList.map((row, i) => (
              <TableRow className='row-order' key={row.id}>
                <TableCell>{row.product_name}</TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.price)}</TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.quantity_all)}</TableCell>
                <TableCell align='left'>{row.unit_name}</TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSummary = () => {
    return (
      <TableContainer sx={{ boxShadow: 0 }}>
        <Table aria-label='summary table'>
          <TableBody>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>Total :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>{Func.numberDisplay(cart.total)}</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>Discount :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>{Func.numberDisplay(cart.discount_all)}</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>Net total :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>{Func.numberDisplay(cart.nettotal)}</MDTypography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const numberShow = (inp) => {
    if (Func.isInt(inp * 1)) {
      return <span className='nowrap'>{Func.numberDisplay(inp)} .-</span>;
    }

    return <span>{Func.numberDisplay(inp, 2)}</span>;
  };

  const renderChange = () => {
    if (cart.money_change > 0) {
      return (
        <MDBox className='box-change' onClick={() => window.location.reload()}>
          <div className='label'>เงินทอน (Change)</div>
          <div className='amount'>{numberShow(cart.money_change)}</div>
          <div className='baht'>บาท (Baht)</div>
        </MDBox>
      );
    }

    return (
      <MDBox className='box-total' onClick={() => window.location.reload()}>
        <div className='label'>ยอดชำระ (Net total)</div>
        <div className='amount'>{numberShow(cart.nettotal)}</div>
        <div className='baht'>บาท (Baht)</div>
      </MDBox>
    );
  };

  const renderCampaign = () => {
    return (
      <MDBox mt={1}>
        <TableContainer sx={{ height: attr.poscampaign_height * 1, boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Promotion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaign.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell>{row.promotion_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderSlideshow = () => {
    const settings = {
      autoplay: true,
      autoplaySpeed: 3000,
      className: 'center',
      infinite: true,
      centerPadding: '60px',
      slidesToShow: 1,
      speed: 500,
      arrows: false,
    };
    const cssImg = { width: '100%', borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px' };
    return (
      <MDBox>
        <Slider {...settings}>
          {promotion &&
            promotion.map((item) => (
              <div key={item.id}>
                <img src={item.image} style={cssImg} alt={item.name} height='160' />
              </div>
            ))}
        </Slider>
      </MDBox>
    );
  };

  const renderVideo = () => {
    return (
      <MDBox mt={1} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <video controls src={vdo.filepath} width='100%' loop muted>
          <track default kind='captions' />
        </video>
      </MDBox>
    );
  };

  const renderReload = () => {
    return (
      <Fab
        size='small'
        color='secondary'
        aria-label='client'
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
        }}
        onClick={() => window.location.reload()}
      >
        <Icon>refresh</Icon>
      </Fab>
    );
  };

  const renderScreen = () => {
    return (
      <MDBox p={1} sx={{ backgroundColor: attr.pos_bg }}>
        <Grid container spacing={1}>
          <Grid item xs={8}>
            <img
              src={attr.image_postop}
              alt=''
              width='100%'
              height='185'
              style={{ display: 'block', borderRadius: '12px' }}
            />
          </Grid>
          <Grid item xs={4}>
            {renderChange()}
          </Grid>
          <Grid item xs={5}>
            {renderOrder()}
          </Grid>
          <Grid item xs={3}>
            {renderSummary()}
            {renderCampaign()}
          </Grid>
          <Grid item xs={4}>
            <img
              src={attr.image_posright}
              alt=''
              width='100%'
              style={{ display: 'block', borderRadius: '12px' }}
            />
            {renderVideo()}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: 1 }}>
          <Grid item xs={8}>
            {renderSlideshow()}
          </Grid>
          <Grid item xs={4}>
            <img
              src={attr.image_posbottom}
              alt=''
              width='100%'
              height='160'
              style={{
                display: 'block',
                borderTopRightRadius: '12px',
                borderBottomRightRadius: '12px',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  useEffect(() => {
    handleCart();
  }, [app.device]);

  return <PageLayout>{app.device === '' ? renderReload() : renderScreen()}</PageLayout>;
}

export default PosMiniScreen;
